import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useOutsideClick } from '@shared/hooks';
import { SearchInput } from '../search';
import { Icon } from '@shared/widgets/icon';
import ChevronIcon from '@assets/icons/chevron.svg';
import countriesData from '@assets/data/countries.json';

interface IPhoneInputProps {
  value: { countryCode: string; phoneNumber: string };
  onChange: (value: { countryCode: string; phoneNumber: string }) => void;
  enableSearch?: boolean;
}

export const PhoneInput = ({ value, onChange, enableSearch }: IPhoneInputProps) => {
  const [countries] = useState(countriesData);
  const [dropdown, setDropdown] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => setDropdown(false));

  const changeDropdownItem = (country: { code: number; name: string; shortName: string; flag: string }) => {
    setDropdown(false);
    setName(country.shortName);
    onChange({ ...value, countryCode: country.code.toString() });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, phoneNumber: e.target.value });
  };

  const currentItem = useMemo(() => countries.find((item) => item.code.toString() === value.countryCode), [countries, value.countryCode]);
  const filteredCountries = useMemo(() => {
    return countries.filter((country) => `${country.shortName} +${country.code}`.toLowerCase().includes(searchValue.trim().toLowerCase()));
  }, [countries, searchValue]);

  return (
    <div ref={ref} className='w-full h-[37px] border-b border-b-light rounded-sm bg-white dark:bg-soft-black flex relative'>
      <div
        className='whitespace-nowrap w-fit py-2 pl-2 flex items-center gap-1 text-soft-black dark:text-white hover:text-white-signal-black active:text-almost-black dark:hover:text-light-telegray dark:active:text-pearlescent-light-gray cursor-pointer select-none transition-all duration-200 ease-in-out'
        onClick={() => setDropdown((prev) => !prev)}
      >
        {`${name || currentItem?.shortName || ''} +${currentItem?.code || ''}`}
        <Icon icon={<ChevronIcon />} className='-rotate-90' />
      </div>
      <input
        className='w-full p-2 bg-ice dark:bg-soft-black no-arrows focus:outline-none'
        type='number'
        placeholder='Phone number'
        value={value.phoneNumber}
        onChange={handlePhoneChange}
      />
      {dropdown && (
        <ul className='w-full max-h-40 m-0 p-0 border bg-white dark:bg-soft-black overflow-auto no-scrollbar absolute top-[37px] left-0 z-10'>
          {enableSearch && (
            <li className='border-b bg-white dark:bg-soft-black sticky top-0 left-0'>
              <SearchInput value={searchValue} onChange={setSearchValue} />
            </li>
          )}
          {filteredCountries.map((country, idx) => (
            <li
              key={idx}
              className={clsx(
                'cursor-pointer py-2.5 px-3 flex items-center justify-between text-sm hover:bg-yellow-brown focus:bg-yellow-brown dark:text-white text-soft-black',
                value.countryCode === country.code.toString() && 'bg-dark-yellow-brown hover:bg-yellow-brown focus:bg-yellow-brown',
              )}
              onClick={() => changeDropdownItem(country)}
            >
              {`${country.shortName} +${country.code}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
